<template>
  <div class="p-projects-ranks">
    <h3 class="p-projects-ranks__title">検索順位チェック</h3>
    <Loading v-if="isLoading" variant="inline" />
    <template v-else>
      <dl class="p-projects-ranks-list p-projects-ranks-list--up">
        <dt class="p-projects-ranks-list__title">
          5日前より順位アップ<Icon2 variant="up" size="11" />
        </dt>
        <dd class="p-projects-ranks-list__content">
          <template v-if="rankUpKeyword">
            <span class="c-text--ellipsis">
              {{ rankUpKeyword }}
            </span>
            のキーワードで順位が上がったページがあります
          </template>
          <template v-else>
            順位の変動はありません
          </template>
        </dd>
      </dl>
      <dl class="p-projects-ranks-list p-projects-ranks-list--down">
        <dt class="p-projects-ranks-list__title">
          5日前より順位ダウン<Icon2 variant="down" size="11" />
        </dt>
        <dd class="p-projects-ranks-list__content">
          <template v-if="rankDownKeyword">
            <span class="c-text--ellipsis">
              {{ rankDownKeyword }}
            </span>
            のキーワードで順位が下がったページがあります
          </template>
          <template v-else>
            順位の変動はありません
          </template>
        </dd>
      </dl>
    </template>
  </div>
</template>

<script>
import Icon2 from '@/components/Icon2';
import Loading from '@/components/Loading';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import axios from 'axios';

export default {
  props: ['project'],
  components: {
    Icon2,
    Loading
  },
  data() {
    return {
      cancelTokens: {
        keywords: null,
        ranks: []
      },
      data: {
        items: []
      },
      period: [],
      isLoading: true
    };
  },
  computed: {
    rankDownKeyword() {
      if (this.isLoading) {
        return '';
      }
      let keyword = '';
      const items = this.data.items;
      for (let i = 0; i < items.length; i++) {
        let position1, position2;
        for (let j = 0; j < items[i].dates.length; j++) {
          const date = utils.getDateResetHours(items[i].dates[j].date);
          if (date.getTime() === this.period[0].getTime()) {
            position1 = items[i].dates[j].position;
          }
          if (date.getTime() === this.period[1].getTime()) {
            position2 = items[i].dates[j].position;
          }
        }
        // 数が大きくなった = 順位が下がった
        if (position1 && position2 && position1 < position2) {
          keyword = items[i].keyword;
          break;
        }
      }
      return keyword;
    },
    rankUpKeyword() {
      if (this.isLoading) {
        return '';
      }
      let keyword = '';
      const items = this.data.items;
      for (let i = 0; i < items.length; i++) {
        let position1, position2;
        for (let j = 0; j < items[i].dates.length; j++) {
          const date = utils.getDateResetHours(items[i].dates[j].date);
          if (date.getTime() === this.period[0].getTime()) {
            position1 = items[i].dates[j].position;
          }
          if (date.getTime() === this.period[1].getTime()) {
            position2 = items[i].dates[j].position;
          }
        }
        // 数が小さくなった = 順位が上がった
        if (position1 && position2 && position1 > position2) {
          keyword = items[i].keyword;
          break;
        }
      }
      return keyword;
    }
  },
  created() {
    // 環境がdemoの場合はデモ用の表示期間で固定するようにする
    if (process.env.VUE_APP_ENV === 'demo') {
      this.period = utils.getGSCDemoPeriod(5);
    } else {
      this.period = utils.getGSCPeriod(5);
    }
    this.getKeywords();
  },
  destroyed() {
    if (this.cancelTokens.keywords) {
      this.cancelTokens.keywords.cancel();
    }
    this.cancelTokens.ranks.forEach(cancelToken => {
      if (cancelToken) {
        cancelToken.cancel();
      }
    });
  },
  methods: {
    getKeywords() {
      this.cancelTokens.keywords = axios.CancelToken.source();
      return apiProjects.observationKeywords
        .get(
          this.auth.idToken,
          this.project.id,
          this.cancelTokens.keywords.token
        )
        .then(response => {
          this.data.items = response.data.items.map(item => {
            item.dates = null;
            return item;
          });
          if (response.data.items.length) {
            this.getRanks();
          } else {
            this.isLoading = false;
          }
        })
        .catch(error => {
          this.isLoading = false;
        });
    },
    async getRanks() {
      this.isLoading = true;

      const allItems = this.data.items;
      const chunkSize = 20;
      const splitLength = Math.ceil(allItems.length / chunkSize);

      for (let splitIndex = 0; splitIndex < splitLength; splitIndex++) {
        // 既にコンポーネントが破棄されていたら終了する
        if (this._isDestroyed) {
          break;
        }
        const splitItems = allItems.slice(
          splitIndex * chunkSize,
          (splitIndex + 1) * chunkSize
        );
        await Promise.all(
          splitItems.map((item, itemIndex) => {
            return this.getRank(item.id, splitIndex * chunkSize + itemIndex);
          })
        );
      }

      this.isLoading = false;
    },
    getRank(id, index) {
      const params = {
        from: this.$options.filters.dateText(this.period[0]),
        to: this.$options.filters.dateText(this.period[1]),
        country: 'JPN',
        device: 'desktop'
      };
      this.cancelTokens.ranks[index] = axios.CancelToken.source();

      return apiProjects.observationKeywords.detail
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          id,
          this.cancelTokens.ranks[index].token,
          index + 1
        )
        .then(response => {
          const startTime = this.period[0].getTime();
          const endTime = this.period[1].getTime();
          const dates = response.data.items.filter(date => {
            const time = utils.getDateResetHours(date.date).getTime();
            if (time >= startTime && time <= endTime) {
              return true;
            }
          });
          Object.assign(this.data.items[index], { dates: dates });
        })
        .catch(error => {
          Object.assign(this.data.items[index], { dates: [] });
        });
    }
  }
};
</script>
